import { FC, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Link, useNavigate, useOutletContext } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Modal, Svg } from '@hopskipdrive/react-components';
import { ButtonLink } from '@rideiq/shared/components';
import { Button } from '@rideiq/shared/components/Button';

import { ApiResponse } from '../../../api/utils/responseTransform';
import { useFlags } from '../../../contexts/LDContext';
import { useToastStore } from '../../../shared/stores/toastStore';
import { UserForm } from '../../components/UserForm';
import { IUserFormType } from '../../components/UserForm/types';
import { userInfoSchema } from '../../components/UserForm/validationSchema';
import { JobTitleOption } from '../../hooks/useJobTitles';
import { setServerFormErrors } from '../../utils/setServerFormErrors';

type ContextType = {
  organizerInfo?: IUserFormType;
  rolesInfo: {
    roleOptions: { value: string; label: string }[];
    roleDescriptionMap: Map<string | null, string>;
    isFetching: boolean;
  };
  jobTitlesInfo: {
    jobTitleOptions: JobTitleOption[];
    isFetching: boolean;
  };
  updateOrganizer: (data) => Promise<ApiResponse>;
};

function useEditOrganizer() {
  return useOutletContext<ContextType>();
}

export const EditOrganizerProfile: FC = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { organizerInfo, updateOrganizer, rolesInfo, jobTitlesInfo } = useEditOrganizer();
  const navigate = useNavigate();
  const { addToast } = useToastStore();
  const { enableDailyQueueAssignedLocations } = useFlags();

  const methods = useForm<IUserFormType>({
    defaultValues: useMemo(() => {
      return organizerInfo;
    }, [organizerInfo]),
    resolver: yupResolver(userInfoSchema(enableDailyQueueAssignedLocations)),
  });

  const { setError } = methods;

  useEffect(() => {
    methods.reset(organizerInfo);
  }, [organizerInfo]);

  const handleSubmit = async (data) => {
    setIsSubmitting(true);
    const { role, defaultAccountLocation, accountLocations, jobTitle, ...rest } = data;
    const payload = {
      ...rest,
      ...(defaultAccountLocation && { defaultAccountLocationId: defaultAccountLocation.id }),
      ...(accountLocations?.length && { accountLocationIds: accountLocations.map((loc) => loc.id) }),
      ...(jobTitle && { jobTitle: jobTitle }),
      roles: [role],
    };

    const response = await updateOrganizer(payload);

    setIsSubmitting(false);
    if (response.type === 'success') {
      navigate(-1);
    } else {
      const validationErrors = response.data?.errors;

      validationErrors && setServerFormErrors({ setError, validationErrors, addToast });
    }
  };

  return (
    <Modal open={true} maxWidth="xl" data-testid="edit-organizer-info">
      <Modal.Title>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          Edit User Information
          <Link to=".." data-testid="closeButton">
            <Svg height="20px" icon="close-outlined" />
          </Link>
        </Box>
      </Modal.Title>
      <Modal.Content>
        <FormProvider {...methods}>
          <UserForm
            handleSubmit={handleSubmit}
            rolesInfo={rolesInfo}
            jobTitlesInfo={jobTitlesInfo}
            defaultRole={organizerInfo?.role}
          />
        </FormProvider>
      </Modal.Content>
      <Modal.Actions>
        <ButtonLink color="tertiary" to="..">
          Cancel
        </ButtonLink>
        <Button type="submit" color="primary" form="user-info-form" loading={isSubmitting}>
          {isSubmitting ? 'Saving...' : 'Save'}
        </Button>
      </Modal.Actions>
    </Modal>
  );
};
